import { IconButton } from '@mui/material';
import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { MinusIcon, PlusIcon } from '../Icons';
import { MAX_QUANTITY } from '@src/Constants';

const baseStyles = css`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 8px;
	padding: 4px 8px;
	min-width: 113px;
`;

const StyledContainer = styled.div`
	${baseStyles}
	border-radius: 3px;
	border: 1.5px solid var(--gray-g-40, #e9e9e9);
	background: var(--gray-00, #fff);
`;

const RoundContainer = styled.div`
	${baseStyles}
	border-radius: 50px;
	border: 1px solid #f1f1f1;
	background: #f7f5f4;
	width: 113px;
	height: 35px;
`;

const StyledInput = styled.input<{ inputWidth: number }>`
	-moz-appearance: textfield; /* Firefox */
	appearance: none;
	&::-webkit-outer-spin-button,
	&::-webkit-inner-spin-button {
		-webkit-appearance: none; /* Chrome, Safari, Edge, Opera */
		margin: 0;
	}
	width: ${(props) => Math.min(props.inputWidth, 60)}px; /* Limit width to 60px */
	text-align: center;
	border: none;
	background: transparent;
	font-size: 16px;
	color: #333;

	&:focus {
		outline: none;
	}
`;

interface CustomCounterProps {
	id?: any;
	counter: number;
	onIncrement: () => void;
	onDecrement: () => void;
	onCounterChange?: (id: string, value: number) => void;
	style?: React.CSSProperties;
	readOnly?: boolean;
	variant?: 'default' | 'round';
	allowZeroQuantity?: boolean;
}

const CustomCounter: FC<CustomCounterProps> = ({
	id,
	counter,
	onIncrement,
	onDecrement,
	onCounterChange,
	style = {},
	readOnly = false,
	variant = 'default',
	allowZeroQuantity = false,
}) => {
	const Container = variant === 'round' ? RoundContainer : StyledContainer;
	const minQuantity = allowZeroQuantity ? 0 : 1;

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		let value = event.target.value;
		const numericValue = parseInt(value, 10);

		if (!isNaN(numericValue) && numericValue > MAX_QUANTITY) {
			return;
		}

		if (!isNaN(numericValue) && numericValue >= minQuantity) {
			onCounterChange && onCounterChange(id, numericValue);
		} else if (!numericValue) {
			onCounterChange && onCounterChange(id, numericValue);
		}
	};

	// Calculate input width with a cap after 4 characters
	const inputWidth = Math.max(
		30,
		Math.min(counter?.toString().length * 15, 60) // Caps width at 60px
	);

	if (readOnly) {
		return (
			<Container id="counter" style={style}>
				<p>{counter?.toString().padStart(2, '0')}</p>
			</Container>
		);
	}

	return (
		<Container id="counter" style={{ ...style, width: `${inputWidth + 80}px` }}>
			<IconButton id="counter-btn-1" onClick={onDecrement} disabled={counter === minQuantity}>
				<MinusIcon id="counter1" stroke={counter === minQuantity ? '#FFE599' : '#FFBE00'} />
			</IconButton>
			{variant === 'round' ? (
				<StyledInput
					id="counter-input"
					type="number"
					value={counter}
					onChange={handleChange}
					min={minQuantity}
					inputWidth={inputWidth}
				/>
			) : (
				<p>{counter}</p>
			)}
			<IconButton id="counter-btn-2" onClick={onIncrement}>
				<PlusIcon id="counter2" />
			</IconButton>
		</Container>
	);
};

export default CustomCounter;
